import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { snapshotsAPI } from '../api/client';
import {
  DIRECTION_SLIPPING,
  SCHEMA_STATE_DIRECTION,
  STATE_THRIVING,
  isAttention,
} from '../lib/snapshotFlags';
import InsetItem, { InsetMeta } from './InsetItem';
import type { Snapshot, SnapshotRating } from '../types';

function currentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

function firstWatch(ratings: SnapshotRating[] | undefined): SnapshotRating | null {
  return (ratings || []).find((rating) => !rating.skipped && isAttention(rating.state, rating.direction)) || null;
}

function thrivingNames(ratings: SnapshotRating[] | undefined): string[] {
  return (ratings || [])
    .filter((rating) => !rating.skipped && rating.state === STATE_THRIVING)
    .map((rating) => rating.category_name)
    .filter(Boolean);
}

function decliningNames(ratings: SnapshotRating[] | undefined): string[] {
  return (ratings || [])
    .filter((rating) => !rating.skipped && (
      rating.direction === DIRECTION_SLIPPING || isAttention(rating.state, rating.direction)
    ))
    .map((rating) => rating.category_name)
    .filter(Boolean);
}

export default function SnapshotHomeCard() {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    snapshotsAPI
      .getMySnapshots()
      .then((rows) => {
        if (!cancelled) setSnapshots(rows);
      })
      .catch(() => {
        // Non-blocking: the card still renders an empty state.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const monthKey = currentMonthKey();
  const drafts = snapshots.filter((row) => !row.is_finalized);
  const draft = drafts.find((row) => row.month === monthKey) || drafts[0] || null;
  const latest = snapshots
    .filter((row) => row.is_finalized)
    .sort((a, b) => b.month.localeCompare(a.month) || b.version - a.version)[0] || null;
  const thisMonth = latest && latest.month === monthKey;
  const isV2 = latest?.schema_version === SCHEMA_STATE_DIRECTION;
  const watch = latest && isV2 ? firstWatch(latest.ratings) : null;
  const thriving = latest && isV2 ? thrivingNames(latest.ratings) : [];
  const declining = latest && isV2 ? decliningNames(latest.ratings) : [];
  const snapshotHref = latest ? `/snapshots/${latest.id}` : '/snapshots';
  const draftHref = '/snapshots';

  return (
    <div className="bg-white rounded-2xl px-6 py-5 shadow-sm h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-400">Snapshot</p>
        <Link to="/snapshots" className="text-sm text-navy hover:underline">
          All snapshots
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : latest || draft ? (
        <div className="flex-1 flex flex-col">
          <div className="space-y-3">
            {latest && (
              <InsetItem
                title={
                  <Link to={snapshotHref} className="hover:underline">
                    {latest.month_display}
                    {latest.version_label ? ` ${latest.version_label}` : ''}
                  </Link>
                }
                meta={
                  <>
                    <InsetMeta icon={Calendar}>
                      {thisMonth ? 'This month' : 'Latest finalized'}
                    </InsetMeta>
                    {latest.owner_experience_label && (
                      <InsetMeta>{latest.owner_experience_label}</InsetMeta>
                    )}
                    {latest.most_important_category_name && (
                      <InsetMeta>Focus · {latest.most_important_category_name}</InsetMeta>
                    )}
                    {thriving.length > 0 && (
                      <InsetMeta>Thriving · {thriving.slice(0, 2).join(', ')}</InsetMeta>
                    )}
                    {declining.length > 0 && (
                      <InsetMeta>Watch · {declining.slice(0, 2).join(', ')}</InsetMeta>
                    )}
                    {!thriving.length && !declining.length && watch && (
                      <InsetMeta>Watch · {watch.category_name}</InsetMeta>
                    )}
                    {!isV2 && latest.overall_delta != null && latest.overall_delta !== 0 && (
                      <InsetMeta>
                        {latest.overall_delta > 0 ? 'Up' : 'Down'} {Math.abs(latest.overall_delta).toFixed(1)}
                      </InsetMeta>
                    )}
                    {!isV2 && latest.best_growth_area && (
                      <InsetMeta>Growing · {latest.best_growth_area.name}</InsetMeta>
                    )}
                    {!isV2 && latest.needs_attention_area && (
                      <InsetMeta>Watch · {latest.needs_attention_area.name}</InsetMeta>
                    )}
                  </>
                }
              />
            )}
            {draft && (
              <InsetItem
                title={
                  <Link to={draftHref} className="hover:underline">
                    Draft in progress
                  </Link>
                }
                meta={
                  <>
                    <InsetMeta icon={Calendar}>
                      {draft.month_display || 'This month'}
                    </InsetMeta>
                    <InsetMeta>{Math.round(draft.wizard_progress || 0)}% complete</InsetMeta>
                  </>
                }
              />
            )}
          </div>
          <div className="mt-auto pt-4">
            {draft ? (
              <Link to="/snapshots" className="text-sm font-medium text-navy hover:underline">
                Continue snapshot
              </Link>
            ) : thisMonth ? (
              <Link to={`/snapshots/${latest.id}`} className="text-sm font-medium text-navy hover:underline">
                View snapshot
              </Link>
            ) : (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <Link to="/snapshots" className="text-sm font-medium text-navy hover:underline">
                  Start this month’s snapshot
                </Link>
                <Link to="/snapshots/blank" className="text-sm font-medium text-navy hover:underline">
                  Print a blank one
                </Link>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          <p className="text-lg font-semibold text-navy">No snapshot yet</p>
          <p className="text-sm text-gray-500 mt-1">
            After you finish one, this card shows your latest month at a glance.
          </p>
          <div className="mt-auto pt-4 flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/snapshots" className="text-sm font-medium text-navy hover:underline">
              Start a snapshot
            </Link>
            <Link to="/snapshots/blank" className="text-sm font-medium text-navy hover:underline">
              Print a blank one
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
